import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { BookmarkFolder, Bookmark } from '@ext-bookmark/shared/types';
import {
  listBookmarkFolders,
  createBookmarkFolder,
  updateBookmarkFolder,
  deleteBookmarkFolder,
  listBookmarks,
  createBookmark,
  updateBookmark,
  deleteBookmark,
  checkBookmarkDuplicate,
} from '../../services/bookmark/bookmarkService';

const STALE = 60 * 1000;
const GC = 5 * 60 * 1000;

export const bookmarkQueryKeys = {
  folders: (userId: string) => ['bookmarks', 'folders', userId] as const,
  list: (userId: string, folderId: string | null) => ['bookmarks', 'list', userId, folderId] as const,
};

export function useBookmarkFoldersQuery(userId: string | null) {
  return useQuery({
    queryKey: bookmarkQueryKeys.folders(userId ?? ''),
    queryFn: async (): Promise<BookmarkFolder[]> => {
      if (!userId) return [];
      const { folders, error } = await listBookmarkFolders(userId);
      if (error) throw error;
      return folders;
    },
    enabled: !!userId,
    staleTime: STALE,
    gcTime: GC,
  });
}

export function useBookmarksQuery(userId: string | null, folderId: string | null) {
  return useQuery({
    queryKey: bookmarkQueryKeys.list(userId ?? '', folderId),
    queryFn: async (): Promise<Bookmark[]> => {
      if (!userId) return [];
      const { bookmarks, error } = await listBookmarks(userId, folderId);
      if (error) throw error;
      return bookmarks;
    },
    enabled: !!userId,
    staleTime: STALE,
    gcTime: GC,
  });
}

export function useCreateBookmarkFolderMutation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { name: string; parentId?: string | null; position?: number }) => {
      const { folder, error } = await createBookmarkFolder(userId, params);
      if (error) throw error;
      return folder!;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookmarkQueryKeys.folders(userId) });
    },
  });
}

export function useUpdateBookmarkFolderMutation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      folderId,
      params,
    }: {
      folderId: string;
      params: { name?: string; parentId?: string | null; position?: number };
    }) => {
      const { folder, error } = await updateBookmarkFolder(folderId, params);
      if (error) throw error;
      return folder!;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookmarkQueryKeys.folders(userId) });
    },
  });
}

export function useDeleteBookmarkFolderMutation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (folderId: string) => {
      const err = await deleteBookmarkFolder(folderId);
      if (err) throw err;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookmarkQueryKeys.folders(userId) });
      qc.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

export function useCreateBookmarkMutation(userId: string, folderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { name: string; url: string; note?: string; position?: number }) => {
      const { bookmark, error } = await createBookmark(userId, folderId, params);
      if (error) throw error;
      return bookmark!;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookmarkQueryKeys.list(userId, folderId) });
      qc.invalidateQueries({ queryKey: bookmarkQueryKeys.folders(userId) });
    },
  });
}

export function useUpdateBookmarkMutation(userId: string, folderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bookmarkId,
      params,
    }: {
      bookmarkId: string;
      params: { name?: string; url?: string; note?: string; position?: number; folderId?: string };
    }) => {
      const { bookmark, error } = await updateBookmark(bookmarkId, params);
      if (error) throw error;
      return bookmark!;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookmarkQueryKeys.list(userId, folderId) });
      qc.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

export function useDeleteBookmarkMutation(userId: string, folderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bookmarkId: string) => {
      const err = await deleteBookmark(bookmarkId);
      if (err) throw err;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookmarkQueryKeys.list(userId, folderId) });
      qc.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

export async function checkDuplicate(
  folderId: string,
  name: string,
  url: string,
  excludeId?: string
): Promise<boolean> {
  const { duplicate, error } = await checkBookmarkDuplicate(folderId, name, url, excludeId);
  if (error) return false;
  return duplicate;
}
